import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  IconButton,
  Collapse,
  useDisclosure,
  Avatar,
  Tooltip,
  useClipboard,
  Spinner,
  Alert,
  AlertIcon,
  Progress,
  Fade
} from '@chakra-ui/react';
import {
  FiUser,
  FiCpu,
  FiChevronDown,
  FiChevronUp,
  FiCopy,
  FiRefreshCw,
  FiThumbsUp,
  FiThumbsDown,
  FiClock,
  FiZap,
  FiDatabase
} from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Source {
  filename: string;
  collection: string;
  relevance_score: number;
  chunk_id: number;
  text_preview: string;
  source_file?: string;
  document_type?: string;
  file_size?: string;
  last_modified?: string;
  content_type?: string;
  processing_mode?: string;
  has_ai_analysis?: boolean;
  ai_tokens_used?: number;
  processing_date?: string;
  file_hash?: string;
  is_recent?: boolean;
  reliability_score?: number;
}

interface Performance {
  total_duration_seconds?: number;
  api_duration_seconds?: number;
  tokens_per_second?: number;
}

interface DebugInfo {
  request_id?: string;
  question_analysis?: {
    type: string;
    complexity: string;
  };
  context_chunks_used?: number;
  system_message_length?: number;
  timestamp?: string;
}

interface ErrorInfo {
  error_type?: string;
  error_message?: string;
  suggestions?: string[];
  timestamp?: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: Source[];
  model_used?: string;
  tokens_used?: number;
  performance?: Performance;
  debug_info?: DebugInfo;
  error_info?: ErrorInfo;
  status?: 'sending' | 'processing' | 'completed' | 'error';
}

interface ChatMessageProps {
  message: Message;
  isLatest?: boolean;
  onRegenerateResponse?: () => void;
  onMessageReaction?: (reaction: 'like' | 'dislike') => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  isLatest = false,
  onRegenerateResponse,
  onMessageReaction
}) => {
  const { isOpen: isSourcesOpen, onToggle: onSourcesToggle } = useDisclosure();
  const { isOpen: isDebugOpen, onToggle: onDebugToggle } = useDisclosure();
  const { hasCopied, onCopy } = useClipboard(message.content);
  const [reaction, setReaction] = useState<'like' | 'dislike' | null>(null);

  const isUser = message.role === 'user';
  const isProcessing = message.status === 'processing' || message.status === 'sending';
  const hasError = message.status === 'error' || message.error_info;

  const handleReaction = (reactionType: 'like' | 'dislike') => {
    setReaction(reactionType);
    onMessageReaction?.(reactionType);
  };

  const getReliabilityColor = (score?: number) => {
    if (!score) return 'gray';
    if (score >= 0.8) return 'green';
    if (score >= 0.6) return 'yellow';
    return 'red';
  };

  const getReliabilityLabel = (score?: number) => {
    if (!score) return 'Desconhecida';
    if (score >= 0.8) return 'Alta';
    if (score >= 0.6) return 'Média';
    return 'Baixa';
  };

  return (
    <Fade in={true}>
      <HStack
        align="start"
        justify={isUser ? 'flex-end' : 'flex-start'}
        w="full"
        spacing={3}
      >
        {!isUser && (
          <Avatar
            size="sm"
            icon={<FiCpu />}
            bg="brand.500"
            color="white"
          />
        )}

        <Box
          maxW="85%"
          bg={isUser ? 'brand.500' : hasError ? 'red.50' : 'white'}
          color={isUser ? 'white' : 'gray.800'}
          borderRadius="2xl"
          p={4}
          border={!isUser ? '1px solid' : 'none'}
          borderColor={hasError ? 'red.200' : 'gray.200'}
          boxShadow={!isUser ? 'sm' : 'md'}
          position="relative"
        >
          {/* Status indicator for processing */}
          {isProcessing && (
            <HStack spacing={2} mb={3} opacity={0.7}>
              <Spinner size="xs" />
              <Text fontSize="xs">
                {message.status === 'sending' ? 'Enviando...' : 'Processando...'}
              </Text>
            </HStack>
          )}

          {/* Error alert */}
          {hasError && message.error_info && (
            <Alert status="error" size="sm" mb={3} borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">{message.error_info.error_message}</Text>
            </Alert>
          )}

          {/* Message content */}
          <Box mb={!isUser && (message.sources || message.performance) ? 3 : 0}>
            {isUser ? (
              <Text whiteSpace="pre-wrap">{message.content}</Text>
            ) : (
              <Box className="markdown-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Estilo customizado para código
                    code: ({ children, className }) => {
                      const isInline = !className;
                      return (
                        <Box
                          as={isInline ? 'code' : 'pre'}
                          bg={isInline ? 'gray.100' : 'gray.900'}
                          color={isInline ? 'gray.800' : 'green.200'}
                          px={isInline ? 1 : 3}
                          py={isInline ? 0 : 2}
                          borderRadius="md"
                          fontSize="sm"
                          fontFamily="mono"
                          overflowX={isInline ? 'visible' : 'auto'}
                          whiteSpace={isInline ? 'normal' : 'pre'}
                        >
                          {children}
                        </Box>
                      );
                    }
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </Box>
            )}
          </Box>

          {/* Assistant message metadata and actions */}
          {!isUser && !isProcessing && (
            <VStack spacing={3} align="stretch">
              {/* Performance and model info */}
              <HStack justify="space-between" fontSize="xs" color="gray.600">
                <HStack spacing={3}>
                  <HStack spacing={1}>
                    <FiCpu size="12px" />
                    <Text>{message.model_used?.split('/')[1] || message.model_used}</Text>
                  </HStack>
                  
                  {message.tokens_used && (
                    <HStack spacing={1}>
                      <FiZap size="12px" />
                      <Text>{message.tokens_used} tokens</Text>
                    </HStack>
                  )}
                  
                  {message.performance?.total_duration_seconds && (
                    <HStack spacing={1}>
                      <FiClock size="12px" />
                      <Text>{message.performance.total_duration_seconds.toFixed(1)}s</Text>
                    </HStack>
                  )}
                </HStack>

                {/* Action buttons */}
                <HStack spacing={1}>
                  <Tooltip label={hasCopied ? 'Copiado!' : 'Copiar'}>
                    <IconButton
                      aria-label="Copiar"
                      icon={<FiCopy />}
                      size="xs"
                      variant="ghost"
                      onClick={onCopy}
                      color={hasCopied ? 'green.500' : 'gray.400'}
                    />
                  </Tooltip>

                  {isLatest && onRegenerateResponse && (
                    <Tooltip label="Regenerar resposta">
                      <IconButton
                        aria-label="Regenerar"
                        icon={<FiRefreshCw />}
                        size="xs"
                        variant="ghost"
                        onClick={onRegenerateResponse}
                      />
                    </Tooltip>
                  )}

                  <Tooltip label="Gostei">
                    <IconButton
                      aria-label="Like"
                      icon={<FiThumbsUp />}
                      size="xs"
                      variant="ghost"
                      onClick={() => handleReaction('like')}
                      color={reaction === 'like' ? 'green.500' : 'gray.400'}
                    />
                  </Tooltip>

                  <Tooltip label="Não gostei">
                    <IconButton
                      aria-label="Dislike"
                      icon={<FiThumbsDown />}
                      size="xs"
                      variant="ghost"
                      onClick={() => handleReaction('dislike')}
                      color={reaction === 'dislike' ? 'red.500' : 'gray.400'}
                    />
                  </Tooltip>
                </HStack>
              </HStack>

              {/* Performance bar */}
              {message.performance?.total_duration_seconds && (
                <Box>
                  <Progress
                    value={(message.performance.api_duration_seconds || 0) / message.performance.total_duration_seconds * 100}
                    size="xs"
                    colorScheme="blue"
                    borderRadius="full"
                    bg="gray.100"
                  />
                  <HStack justify="space-between" fontSize="2xs" color="gray.500" mt={1}>
                    <Text>API: {message.performance.api_duration_seconds?.toFixed(1)}s</Text>
                    <Text>{message.performance.tokens_per_second?.toFixed(0)} tokens/s</Text>
                  </HStack>
                </Box>
              )}

              {/* Sources toggle */}
              {message.sources && message.sources.length > 0 && (
                <HStack justify="space-between">
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={onSourcesToggle}
                    rightIcon={isSourcesOpen ? <FiChevronUp /> : <FiChevronDown />}
                    leftIcon={<FiDatabase />}
                  >
                    {message.sources.length} fonte{message.sources.length > 1 ? 's' : ''}
                  </Button>

                  {message.debug_info && (
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={onDebugToggle}
                      rightIcon={isDebugOpen ? <FiChevronUp /> : <FiChevronDown />}
                    >
                      Debug
                    </Button>
                  )}
                </HStack>
              )}

              {/* Sources */}
              {message.sources && (
                <Collapse in={isSourcesOpen}>
                  <VStack spacing={3} align="stretch" pt={2}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                      Fontes utilizadas:
                    </Text>
                    {message.sources.map((source, index) => {
                      const reliabilityScore = source.reliability_score || 0;
                      const reliabilityColor = getReliabilityColor(reliabilityScore);
                      const reliabilityLabel = getReliabilityLabel(reliabilityScore);

                      return (
                        <Box
                          key={index}
                          p={3}
                          bg="gray.50"
                          borderRadius="md"
                          borderLeft="3px solid"
                          borderLeftColor={`${reliabilityColor}.400`}
                        >
                          {/* Header */}
                          <HStack justify="space-between" mb={2}>
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="bold" fontSize="sm" color="gray.800">
                                📄 {source.source_file || source.filename}
                              </Text>
                              {source.document_type && (
                                <Text color="gray.600" fontSize="xs">
                                  {source.document_type}
                                </Text>
                              )}
                            </VStack>
                            <VStack align="end" spacing={1}>
                              <Badge size="sm" colorScheme="blue">
                                {(source.relevance_score * 100).toFixed(1)}% relevante
                              </Badge>
                              <Badge size="sm" colorScheme={reliabilityColor}>
                                {reliabilityLabel} ({(reliabilityScore * 100).toFixed(0)}%)
                              </Badge>
                            </VStack>
                          </HStack>

                          {/* Metadata */}
                          <HStack spacing={4} mb={2} fontSize="xs" color="gray.600" flexWrap="wrap">
                            {source.last_modified && (
                              <HStack spacing={1}>
                                <Text>📅 {source.last_modified}</Text>
                                {source.is_recent && (
                                  <Badge colorScheme="green" size="xs">Recente</Badge>
                                )}
                              </HStack>
                            )}
                            {source.file_size && (
                              <Text>📏 {source.file_size}</Text>
                            )}
                            {source.has_ai_analysis && (
                              <Badge colorScheme="purple" size="xs">🤖 IA</Badge>
                            )}
                          </HStack>

                          {/* Content preview */}
                          <Text color="gray.700" fontSize="xs" mb={2} lineHeight="1.4">
                            {source.text_preview}
                          </Text>

                          {/* Footer */}
                          <HStack justify="space-between" fontSize="xs" color="gray.500">
                            <Text>
                              Collection: {source.collection} • Chunk: {source.chunk_id}
                            </Text>
                            {source.processing_date && (
                              <Text>Processado: {source.processing_date}</Text>
                            )}
                          </HStack>
                        </Box>
                      );
                    })}
                  </VStack>
                </Collapse>
              )}

              {/* Debug info */}
              {message.debug_info && (
                <Collapse in={isDebugOpen}>
                  <Box pt={2}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
                      Informações de Debug:
                    </Text>
                    <VStack spacing={2} align="stretch" fontSize="xs">
                      {message.debug_info.request_id && (
                        <HStack justify="space-between">
                          <Text color="gray.600">Request ID:</Text>
                          <Text fontFamily="mono">{message.debug_info.request_id}</Text>
                        </HStack>
                      )}
                      {message.debug_info.question_analysis && (
                        <>
                          <HStack justify="space-between">
                            <Text color="gray.600">Tipo de pergunta:</Text>
                            <Badge colorScheme="purple" size="sm">
                              {message.debug_info.question_analysis.type}
                            </Badge>
                          </HStack>
                          <HStack justify="space-between">
                            <Text color="gray.600">Complexidade:</Text>
                            <Badge colorScheme="blue" size="sm">
                              {message.debug_info.question_analysis.complexity}
                            </Badge>
                          </HStack>
                        </>
                      )}
                      <HStack justify="space-between">
                        <Text color="gray.600">Chunks utilizados:</Text>
                        <Text>{message.debug_info.context_chunks_used || 0}</Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text color="gray.600">System message:</Text>
                        <Text>{message.debug_info.system_message_length || 0} chars</Text>
                      </HStack>
                    </VStack>
                  </Box>
                </Collapse>
              )}

              {/* Error suggestions */}
              {message.error_info?.suggestions && message.error_info.suggestions.length > 0 && (
                <Box pt={2}>
                  <Text fontSize="sm" fontWeight="semibold" color="red.600" mb={2}>
                    Sugestões para resolver:
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    {message.error_info.suggestions.map((suggestion, index) => (
                      <Text key={index} fontSize="xs" color="gray.700">
                        • {suggestion}
                      </Text>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          )}
        </Box>

        {isUser && (
          <Avatar
            size="sm"
            icon={<FiUser />}
            bg="gray.400"
            color="white"
          />
        )}
      </HStack>
    </Fade>
  );
};

export default ChatMessage;